/**
 * Fliplab — keep the variant picker on a real combination
 *
 * Fliplab products have a deliberately SPARSE variant matrix. A 600mm cabinet
 * door, for example, offers 12 Profiles x 5 Finishes x 5 Colours but only 25
 * real variants, because Finish is constrained by Profile and Colour is fully
 * determined by Finish:
 *
 *   Flat                        -> Natural / AbsoluteMatte / AbsoluteGrain
 *   Shaker, Reed, Scallop, ...  -> Polytec Solid Colour / Polytec Woodmatt
 *   Natural -> List A    AbsoluteMatte -> List B    Woodmatt -> List E   ...
 *
 * Primavera's picker leaves the other options untouched when one changes, so
 * going from Flat (Natural / List A) to Shaker left Finish on Natural — a
 * combination with no variant behind it — and the PDP showed "Unavailable"
 * with Add-to-Cart disabled.
 *
 * This script runs on the `change` event in the CAPTURE phase, i.e. before
 * VariantPicker's own listener (theme.js) reads `selectedOptionValues` and
 * before product-page.js fetches the re-rendered section. It rewrites the OTHER
 * options onto the closest real variant, so the single fetch that follows
 * already asks for a combination that exists. There is no second request and
 * no flash of "Unavailable".
 *
 * Choosing the replacement: every candidate variant carrying the shopper's new
 * value is scored on how many of the other currently-selected values it keeps,
 * weighted so that EARLIER options count for more (Profile outranks Finish
 * outranks Colour). In-stock variants always outrank out-of-stock ones. So
 * changing Profile keeps the current Finish wherever that Finish is offered for
 * the new Profile, and only falls back to the Profile's first Finish when it
 * genuinely is not.
 *
 * The matrix itself is emitted by snippets/variant-picker.liquid as
 * <script type="application/json" data-fliplab-variants>.
 *
 * Deliberate non-goals:
 *   - The control the shopper just used is never rewritten.
 *   - A combination that genuinely exists is left alone, even if it is sold out,
 *     so the theme can honestly report "Sold out" rather than silently moving
 *     the shopper somewhere else.
 *   - Easify's Colour swatches live outside <variant-picker>, so Easify's own
 *     synthetic change events are ignored here. They refresh on `variant:change`
 *     via fliplab-easify-bridge.js, which now fires because a variant resolves.
 */
(function () {
  'use strict';

  /** Every option control in the picker, in Shopify option order. */
  function readControls(picker) {
    var controls = [];
    Array.prototype.forEach.call(picker.querySelectorAll('.variant-picker__input'), function (wrap) {
      var select = wrap.querySelector('select[name^="options["]');
      if (select) {
        controls.push({ kind: 'select', el: select, wrap: wrap });
        return;
      }
      // buttons / border / swatch styles render radios instead.
      var radios = wrap.querySelectorAll('input[type="radio"][data-option-value-id]');
      if (radios.length) {
        controls.push({ kind: 'radio', els: Array.prototype.slice.call(radios), wrap: wrap });
      }
    });
    return controls;
  }

  function readMatrix(picker) {
    var node = picker.querySelector('script[data-fliplab-variants]');
    if (!node) return null;
    try {
      var parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * The control's current value. For a select we read the `selected` ATTRIBUTE,
   * because that is what VariantPicker.selectedOptionValues walks — and during
   * capture phase it still points at the pre-change option.
   */
  function valueOf(control) {
    if (control.kind === 'select') {
      var option = control.el.querySelector('option[selected]') || control.el.selectedOptions[0];
      return option ? option.value : null;
    }
    for (var i = 0; i < control.els.length; i++) {
      if (control.els[i].checked) return control.els[i].value;
    }
    return null;
  }

  /** Move a control onto `value`, keeping exactly one selected option. */
  function applyValue(control, value) {
    if (control.kind === 'select') {
      var match = null;
      Array.prototype.forEach.call(control.el.options, function (option) {
        if (option.value === value) match = option;
      });
      // Bail BEFORE mutating: clearing every `selected` attribute without
      // setting a new one would break selectedOptionValues and crash
      // VariantPicker.updateSelectionMetadata() on the next change.
      if (!match) return false;

      Array.prototype.forEach.call(control.el.options, function (option) {
        option.removeAttribute('selected');
      });
      match.setAttribute('selected', 'selected');
      control.el.value = value;

      // Keep the custom-select trigger label in step, so the corrected value is
      // visible immediately instead of only after the section re-renders.
      var button = control.wrap.querySelector('.custom-select__button');
      if (button) button.textContent = match.textContent.trim();
      return true;
    }

    var hit = false;
    control.els.forEach(function (input) {
      var on = input.value === value;
      input.checked = on;
      if (on) hit = true;
    });
    return hit;
  }

  function exists(matrix, combination) {
    for (var i = 0; i < matrix.length; i++) {
      var values = matrix[i].o;
      var same = true;
      for (var j = 0; j < values.length; j++) {
        if (values[j] !== combination[j]) {
          same = false;
          break;
        }
      }
      if (same) return true;
    }
    return false;
  }

  /** Best real variant that carries `newValue` at `changedIndex`. */
  function pickVariant(matrix, current, changedIndex, newValue) {
    var best = null;
    var bestScore = -Infinity;

    for (var i = 0; i < matrix.length; i++) {
      var entry = matrix[i];
      if (entry.o[changedIndex] !== newValue) continue;

      // Out-of-stock variants are a last resort, never a tie-break win.
      var score = entry.a ? 0 : -1000;

      for (var j = 0; j < entry.o.length; j++) {
        if (j === changedIndex) continue;
        if (entry.o[j] === current[j]) {
          // Earlier options are the more primary choice, so preserving them
          // outweighs preserving every later option combined.
          score += 1 << (entry.o.length - 1 - j);
        }
      }

      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    }

    return best;
  }

  function onChange(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var picker = target.closest('variant-picker');
    if (!picker) return;

    var controls = readControls(picker);
    if (controls.length < 2) return;

    var changedIndex = -1;
    for (var i = 0; i < controls.length; i++) {
      var control = controls[i];
      var isTarget = control.kind === 'select' ? control.el === target : control.els.indexOf(target) > -1;
      if (isTarget) {
        changedIndex = i;
        break;
      }
    }
    if (changedIndex < 0) return;

    var matrix = readMatrix(picker);
    if (!matrix) return;
    // Shape mismatch (a stale matrix against a re-rendered picker): stand aside
    // rather than guess.
    if (matrix[0].o.length !== controls.length) return;

    var current = controls.map(valueOf);
    var newValue = controls[changedIndex].kind === 'select' ? controls[changedIndex].el.value : target.value;

    var wanted = current.slice();
    wanted[changedIndex] = newValue;
    // Already a real combination — leave every control exactly as the shopper
    // set it, sold out or not.
    if (exists(matrix, wanted)) return;

    var best = pickVariant(matrix, current, changedIndex, newValue);
    // Nothing in the catalogue offers this value: let the theme report it.
    if (!best) return;

    for (var k = 0; k < controls.length; k++) {
      if (k === changedIndex) continue;
      if (best.o[k] !== current[k]) applyValue(controls[k], best.o[k]);
    }
  }

  // Capture phase on the document: <variant-picker> listens in the bubble
  // phase, so this has already corrected the sibling options by the time it
  // builds selectedOptionValues. Delegated, so it survives the picker being
  // replaced wholesale by HTMLUpdateUtility.viewTransition on every change.
  document.addEventListener('change', onChange, true);
})();
